import { createContext, useContext } from 'react'
import { capturarRetornoDoLogin, gravarSessao } from './armazenamento'
import type { UsuarioLogado } from '../types/gmud'

/**
 * O retorno do login é lido no carregamento do módulo, antes de a árvore React montar.
 *
 * Fazer isso dentro de um componente esbarraria no StrictMode, que invoca inicializador
 * de estado duas vezes: a primeira leitura limparia o fragmento e a segunda devolveria
 * vazio — o usuário voltaria do Entra ID deslogado, e só em desenvolvimento.
 */
const retorno = capturarRetornoDoLogin()
if (retorno?.sessao) {
  gravarSessao(retorno.sessao)
}

/** Mensagem trazida do fluxo de login, quando ele terminou em recusa. */
export const erroDeLoginInicial: string | null = retorno?.erro ?? null

/** O que a aplicação sabe sobre quem está usando. */
export interface EstadoDaSessao {
  usuario: UsuarioLogado | null
  /** Verdadeiro enquanto o `GET /me` da carga inicial não respondeu. */
  carregando: boolean
  autenticado: boolean
  erroDeLogin: string | null
  sair: () => void
  descartarErroDeLogin: () => void
}

export const ContextoDaSessao = createContext<EstadoDaSessao | null>(null)

/**
 * @returns o estado da sessão
 * @throws quando usado fora do `ProvedorDeSessao` — erro de montagem, não de runtime do usuário
 */
export function useSessao(): EstadoDaSessao {
  const estado = useContext(ContextoDaSessao)
  if (!estado) {
    throw new Error('useSessao precisa estar dentro de <ProvedorDeSessao>.')
  }
  return estado
}

/**
 * Permissão grossa, só para decidir se um item de menu aparece.
 *
 * O que se pode fazer com UMA solicitação nunca sai daqui: vem de
 * `/gmuds/{id}/transicoes-possiveis`, calculado pelo mesmo código que a máquina de
 * estados usa para decidir.
 *
 * @param usuario usuário logado
 * @param permissao código da permissão (ex.: GMUD_CRIAR)
 * @returns verdadeiro quando o backend declarou essa permissão para ele
 */
export function temPermissao(usuario: UsuarioLogado | null, permissao: string): boolean {
  return usuario?.permissoes.includes(permissao) ?? false
}
